import { useState } from "react";
import { UI } from "../constants/tokens";
import { useFxRates } from "../utils/fxRates";
import { dateNumeric } from "../utils/formatDate";
import Money, { Amount } from "./Money";
import ShekelSign from "./ShekelSign";
import { Skeleton } from "./Skeleton";

// ─── A dollar figure with its shekels under it (sprint 15, stage 1) ─────────
// The one way a money figure on a value screen carries shekels. The dollar
// figure stays the main number (`children`, or a <Money> drawn from `usd`);
// the shekels are a second line right under it, in full ink, "₪123,863".
// The rate and when it was quoted are not on the line · they are in the
// tooltip, and a tap shows them under it.
//
// Sprint 16, 2.1: about 55% of the dollar's size and regular weight (400,
// from the "Archivo Shekel" face in fonts.css · Archivo's own variable file,
// scoped to this line so no other number changes weight), so the
// shekels read as the translation of the figure above them and never compete
// with it. The ₪ is drawn (ShekelSign), not typed: Archivo has no ₪, so every
// device substituted its own, and in Assistant, the Hebrew text font, it
// stands at 86% of the digits' height and read as a "ש" (measured, 07/10).
//
// `inline` (2.2): the shekels beside the figure, on the same line, instead of
// under it · for a change that sits on a row of its own, where a second line
// under it read as a stray line.
//
// When the line is drawn:
//   · display currency is not ILS (David, 1.6: with ILS chosen the main
//     figure is already in shekels, and a second shekel line would repeat it.
//     With EUR or GBP the line stays, as the shekel line under the portfolio
//     value did before · review)
//   · a live USD/ILS rate exists (1.5: no rate, no line · never a fixed or
//     estimated rate)
//
// `ils`: a shekel figure computed elsewhere, used as given · the gain since
// purchase at the rate of each purchase day (sprint 13, 5.2). `ilsNote` says
// how it was computed, for the tooltip.
// `signed`: a change, not a level · "+₪1,234" / "−₪1,234".
//
// The gain since purchase in shekels (bug round, 17/10/2026) has no stand-in:
// `ilsSince` is shekelSince's answer (utils/purchaseFx.js). While it is
// loading the line is a placeholder, when there is none it is "—" and a tap
// says why, and a figure carries `FX_EFFECT_LABEL` under it. Never the dollar
// gain at today's rate in the meantime.

// 55% of the dollar figure, never smaller than 11px: 55% of a 16px figure is
// 9px, too small to read on a phone.
const RATIO = 0.55;
const MIN_PX = 11;

// The drawn ₪ lives in its own file since sprint 17, so Money can use it too.
export { ShekelSign };

const clock = at => {
  if (!at) return null;
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return null;
  return `${dateNumeric(d, { year: false })} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

// The shekel figure, whole shekels, or null when there is no line to draw.
export function shekelValue(usd, { cur = "USD", fx, ils = null } = {}) {
  if (cur === "ILS" || !fx?.ILS?.rate) return null;
  const v = Number.isFinite(ils) ? ils : Number.isFinite(usd) ? usd * fx.ILS.rate : null;
  return v == null ? null : Math.round(v);
}

export function shekelLine(usd, opts = {}) {
  const v = shekelValue(usd, opts);
  if (v == null) return null;
  // The sign is read after rounding: a change of $0.08 is "₪0", not "+₪0".
  const sign = v < 0 ? "−" : opts.signed && v > 0 ? "+" : "";
  return `${sign}₪${Math.abs(v).toLocaleString("en-US")}`;
}

// A line of text with every "₪" in it drawn by ShekelSign · for notes set in
// the Hebrew text font, where the typed sign reads as a "ש" (2.3).
export function withShekel(text) {
  if (typeof text !== "string" || !text.includes("₪")) return text;
  return text.split("₪").flatMap((part, i) => i === 0 ? [part] : [
    // Named as an image: in a note there is no aria-label around it, and
    // VoiceOver drops zero-size text (review).
    <span key={i} style={{ whiteSpace: "nowrap" }}><ShekelSign title="₪"/></span>, part,
  ]);
}

// "—" in a money slot, with the reason one tap away.
function Missing({ why, size }) {
  const [open, setOpen] = useState(false);
  return (
    <span style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-start", gap: 2 }}>
      <button type="button" onClick={e => { e.stopPropagation(); setOpen(o => !o); }} aria-expanded={open} aria-label={`אין סכום בשקלים · ${why}`}
        style={{ background: "transparent", border: "none", padding: "5px 0", margin: "-5px 0", minHeight: 0, cursor: "pointer", fontFamily: "inherit", fontSize: size, fontWeight: 600, color: UI.text.muted, lineHeight: 1.2 }}>
        —
      </button>
      {open && <span role="note" dir="rtl" style={{ fontSize: 11.5, color: UI.text.secondary, lineHeight: 1.5, maxWidth: 280 }}>{why}</span>}
    </span>
  );
}

const Caption = ({ text }) => (
  <span dir="rtl" style={{ fontSize: 11, color: UI.text.muted, lineHeight: 1.4 }}>{text}</span>
);

// The main figure of a since-purchase change when the display currency IS
// shekels: the same three states as the line under a dollar figure.
export function IlsSinceSlot({ since, size = 16, caption = null, children }) {
  if (since?.state === "loading") return <Skeleton w={Math.round(size * 7)} h={Math.round(size * 1.6)} radius={999} label="טוען"/>;
  if (since?.state === "none") return <Missing why={since.why} size={size}/>;
  if (!caption) return children;
  return (
    <span style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-start", gap: 3 }}>
      {children}
      <Caption text={caption}/>
    </span>
  );
}

export default function DualMoney({
  usd, cur = "USD", size = 16, weight: _weight = 700, color, signed = false,
  ils = null, ilsNote = null, children, primary = {}, style, inline = false,
  ilsSince = null, ilsCaption = null,
}) {
  const fx = useFxRates();
  const [open, setOpen] = useState(false);
  const pendingOrNone = ilsSince && ilsSince.state !== "ready" && cur !== "ILS" && fx?.ILS?.rate;
  const line = pendingOrNone ? null : shekelLine(usd, { cur, fx, ils, signed });
  // A change takes the colour of ITS OWN sign: at the purchase-day rate a gain
  // in dollars can be a loss in shekels (review · green and red say which way
  // the number shown moved).
  const sv = shekelValue(usd, { cur, fx, ils });
  const lineColor = signed ? (sv > 0 ? UI.gain : sv < 0 ? UI.loss : UI.text.primary) : (color || UI.text.primary);
  const main = children ?? <Money value={usd} cur={cur} size={size} weight={_weight} color={color} className="si-num" {...primary}/>;
  const wrap = inline
    ? { display: "inline-flex", flexDirection: "row", alignItems: "baseline", flexWrap: "wrap", columnGap: 8, rowGap: 2, ...style }
    : { display: "inline-flex", flexDirection: "column", alignItems: signed ? "flex-end" : "flex-start", gap: 2, ...style };
  const lineSize = Math.max(MIN_PX, Math.round(size * RATIO));
  if (pendingOrNone) {
    return (
      <span data-dual-money style={{ ...wrap, ...(inline ? { alignItems: "center" } : null) }}>
        {main}
        {ilsSince.state === "loading"
          ? <Skeleton w={Math.round(lineSize * 5.5)} h={Math.round(lineSize * 1.2)} label="טוען"/>
          : <Missing why={ilsSince.why} size={lineSize}/>}
      </span>
    );
  }
  if (!line) return main;

  const r = fx.ILS;
  const how = ilsNote || `לפי שער ${r.rate.toFixed(3)} ₪ לדולר`;
  const when = clock(r.at) || (r.date ? dateNumeric(r.date) : null);
  const tip = [how, when ? `נכון ל-${when}` : null, r.source ? `מקור: ${r.source}` : null].filter(Boolean).join(" · ");

  const shekels = (
    <button type="button" onClick={e => { e.stopPropagation(); setOpen(o => !o); }} title={tip} aria-expanded={open}
      aria-label={`${line} · ${tip}`}
      // Not the 44px phone minimum for buttons (index.css): that stacked an
      // empty 30px under every shekel line and grew each card it sat in.
      // The padding gives a 24px target without taking layout room.
      style={{ background: "transparent", border: "none", padding: "5px 0", margin: "-5px 0", minHeight: 0, cursor: "pointer", fontFamily: "inherit", textAlign: "start", lineHeight: 0 }}>
      {/* The shared amount (Money.jsx · Amount): the ₪ follows the same
          rule as every currency sign, 70% of these digits, on the baseline.
          "Archivo Shekel" draws the line at 400. */}
      <Amount value={sv} currency="ILS" size={lineSize} weight={400} shekelFace
        signed={signed} color={lineColor} style={{ lineHeight: 1.2 }} data-shekels/>
    </button>
  );

  return (
    // A change ("+$450.00 +1.67%") is an LTR run with the dollars on its left:
    // the shekels go under them, on the left too. A level ("$27,400") is
    // aligned with the start of the line.
    // One line wherever the figure itself fits the row (`inline`). Its label
    // is a separate item of the row around it (HomeTab, PortfolioTab), so the
    // shekels drop under the dollars only on a phone too narrow for the
    // figure alone (320px · review).
    <span data-dual-money style={wrap}>
      {main}
      {/* The caption sits under the shekels, not beside them, also when the
          figure and the shekels share a line (`inline`). */}
      {ilsCaption ? (
        <span style={{ display: "inline-flex", flexDirection: "column", alignItems: inline || !signed ? "flex-start" : "flex-end", gap: 1 }}>
          {shekels}
          <Caption text={ilsCaption}/>
        </span>
      ) : shekels}
      {/* RTL of its own: the figure can sit inside an LTR run (a change row
          on the home screen), and the Hebrew note must still read right. */}
      {open && <span role="note" dir="rtl" style={{ fontSize: 11.5, color: UI.text.secondary, lineHeight: 1.5, maxWidth: 280 }}>{withShekel(tip)}</span>}
    </span>
  );
}
